import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BreadcrumbContainer,
  BreadcrumbItem,
  BreadcrumbSeparator,
} from "./Breadcrumb.styled";
import { func } from "../../../utils";
import useWindowSize from "../../../hooks/useWindowSize";

/**
 * Breadcrumb component for navigation
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of breadcrumb items, each with label and path
 * @param {string} props.className - Optional additional CSS class
 * @param {string} props.backgroundColor - Optional background color
 */
const Breadcrumb = ({
  items = [],
  className = "",
  backgroundColor = "transparent",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { size } = useWindowSize();
  const isMobileView = (size?.width || 0) <= 830;
  // Generate items automatically if not provided
  const generateItems = () => {
    if (items.length) return items;

    const paths = location.pathname.split("/").filter(Boolean);
    const generatedItems = [];

    // Add home by default
    generatedItems.push({
      label: "Beranda",
      path: "/",
    });

    // Add current path items
    let currentPath = "";
    paths.forEach((path, i) => {
      currentPath += `/${path}`;
      generatedItems.push({
        label: path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, " "),
        path: currentPath,
      });
    });

    return generatedItems;
  };

  const breadcrumbItems = generateItems();

  const handleClick = (path) => {
    navigate(path);
  };

  // Generate breadcrumb separator arrow icon
  const renderArrowIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.47 12.06L10.53 8L6.47 3.94L5.53 4.88L8.65 8L5.53 11.12L6.47 12.06Z"
        fill="#DDE2E8"
      />
    </svg>
  );

  // Function to determine if a label is too long
  const getItemMaxWidth = (label) => {
    // Count the number of sentences by counting periods, exclamation marks, and question marks
    const sentenceCount = (label.match(/[.!?]+\s/g) || []).length + 1;

    // If more than 2 sentences or long text, set a limited width
    if (sentenceCount > 2 || label.length > 50) {
      return "120px"; // More restrictive for longer content
    }

    // For normal length items, use default (null will trigger the default in styled component)
    return null;
  };

  return (
    <BreadcrumbContainer className={className} style={{ backgroundColor }}>
      {breadcrumbItems.map((item, index) => (
        <React.Fragment key={index}>
          <BreadcrumbItem
            className="capitalize"
            isActive={index === breadcrumbItems.length - 1}
            onClick={() =>
              index !== breadcrumbItems.length - 1 && handleClick(item.path)
            }
            maxWidth={isMobileView ? "100px" : "auto"}
            title={item.label} // Add title attribute for tooltip on hover
          >
            {item.label}
          </BreadcrumbItem>

          {index < breadcrumbItems.length - 1 && (
            <BreadcrumbSeparator>{renderArrowIcon()}</BreadcrumbSeparator>
          )}
        </React.Fragment>
      ))}
    </BreadcrumbContainer>
  );
};

export default Breadcrumb;
